import React from "react";
import { fmt } from "@/lib/api";
import { VISIBLE_TAB_DEFS } from "@/lib/tabsConfig";

/**
 * Excel-style tab strip for the multi-product estimator.
 *
 * The contractor can quote up to three parallel options on one estimate —
 * Vinyl, Ascend, and LP Smart Siding. Each tab holds its own line items so
 * the homeowner can compare options apples-to-apples on one quote.
 *
 * Per-tab subtotal = sum of (qty × (mat + lab)) for that tab's lines + its
 * misc rows. The Grand Total at the bottom of the page still rolls all
 * tabs together so the contractor sees the full quote value.
 *
 * Tab visibility is governed by `TAB_VISIBILITY` in `lib/tabsConfig.js` —
 * flip a single boolean there to hide/show a tab without touching the
 * underlying catalog data, HOVER mappings, or saved estimate lines.
 */
export const TABS = VISIBLE_TAB_DEFS;

function subtotalForTab(est, tabId) {
  const lines = (est?.lines || []).filter((l) => (l.tab || "vinyl") === tabId);
  const miscLab = (est?.misc_labor || []).filter((m) => (m.tab || "vinyl") === tabId);
  const miscMat = (est?.misc_material || []).filter((m) => (m.tab || "vinyl") === tabId);
  const linesSell = lines.reduce(
    (s, l) => s + (l.qty || 0) * ((l.mat || 0) + (l.lab || 0)),
    0
  );
  const miscSell =
    miscLab.reduce((s, m) => s + (m.lab || 0), 0) +
    miscMat.reduce((s, m) => s + (m.mat || 0) + (m.lab || 0), 0);
  return linesSell + miscSell;
}

function filledCountForTab(est, tabId) {
  return (est?.lines || []).filter(
    (l) => (l.tab || "vinyl") === tabId && (l.qty || 0) > 0
  ).length;
}

export default function EstimatorTabs({ est, activeTab, onChange, tabs = TABS, derivedUnappliedTab = null }) {
  return (
    <div
      className="card mb-4 p-2 flex flex-wrap gap-1"
      role="tablist"
      data-testid="estimator-tabs"
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const count = filledCountForTab(est, tab.id);
        const subtotal = subtotalForTab(est, tab.id);
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            data-testid={`estimator-tab-${tab.id}`}
            className={[
              "flex-1 min-w-[140px] px-4 py-3 text-left border transition-colors",
              isActive
                ? "border-[var(--brand)] bg-orange-50"
                : "border-[var(--border)] bg-[var(--surface)] hover:border-[var(--muted)]",
            ].join(" ")}
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className={[
                  "text-xs uppercase tracking-[0.18em] font-bold",
                  isActive ? "text-[var(--brand-text)]" : "text-[var(--ink-2)]",
                ].join(" ")}
              >
                {tab.label}
              </span>
              {count > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-[var(--bg-app)] text-[var(--ink-2)] rounded-sm">
                  {count}
                </span>
              )}
            </div>
            {derivedUnappliedTab === tab.id && subtotal === 0 ? (
              <div
                className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[#92400E]"
                data-testid={`estimator-tab-${tab.id}-derived-unapplied`}
              >
                Derived — not applied
              </div>
            ) : (
              <div
                className={[
                  "mt-1 font-mono-num text-sm",
                  isActive ? "text-[var(--ink)] font-bold" : "text-[var(--muted)]",
                ].join(" ")}
              >
                {fmt(subtotal)}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
